import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';

type GraphIdentity = {
  signInType: string;
  issuer: string;
  issuerAssignedId: string;
};

type GraphUserPayload = {
  id?: string;
  displayName?: string;
  mail?: string | null;
  otherMails?: string[];
  proxyAddresses?: string[];
  userPrincipalName?: string;
  identities?: GraphIdentity[];
};

type GraphUserPatchBody = {
  mail: string;
  identities: GraphIdentity[];
  proxyAddresses?: string[];
};

const DEFAULT_LOOKUP_USER_ID = '650101cd-a897-49f6-91a3-de01f6dbae46';

const GRAPH_USER_FULL_SELECT =
  'id,displayName,mail,otherMails,proxyAddresses,identities,userPrincipalName';

@Component({
  selector: 'app-graph-user-test',
  templateUrl: './graph-user-test.component.html',
  styleUrls: ['./graph-user-test.component.scss'],
})
export class GraphUserTestComponent implements OnInit {
  /** Exposed for template (Graph $select list). */
  readonly fullGraphSelect = GRAPH_USER_FULL_SELECT;

  loading = false;
  patching = false;
  error = '';
  patchError = '';
  userId = '';
  accountsUserId = '';
  email = '';
  graphRequestUrl = '';
  graphAccessToken = '';
  graphResponseJson = '';
  graphStatus: number | null = null;

  graphUser: GraphUserPayload | null = null;
  newEmail = '';
  oldEmail = '';
  includeProxyAddresses = true;
  exchangeWebhookConfigured = false;
  exchangeLoading = false;
  exchangeError = '';
  exchangeResponseJson = '';
  patchRequestJson = '';
  patchResponseJson = '';
  patchStatus: number | null = null;

  lookupUserId = DEFAULT_LOOKUP_USER_ID;
  lookupRequestUrl = '';
  lookupResponseJson = '';
  lookupStatus: number | null = null;
  lookupError = '';
  lookupLoading = false;

  constructor(
    private readonly authService: AuthService,
    private readonly localStorage: LocalStorageService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    if (!this.localStorage.authorizationData?.token) {
      void this.router.navigate(['/login'], {
        queryParams: { returnUrl: '/test/graph-user' },
      });
      return;
    }
    void this.loadAndFetchGraph();
  }

  get canPatchEmail(): boolean {
    return (
      !!this.userId &&
      !!this.graphAccessToken &&
      !!this.newEmail.trim() &&
      !this.loading &&
      !this.patching
    );
  }

  async loadAndFetchGraph(): Promise<void> {
    this.loading = true;
    this.error = '';
    this.patchError = '';
    this.graphResponseJson = '';
    this.graphStatus = null;
    this.patchResponseJson = '';
    this.patchStatus = null;
    this.patchRequestJson = '';
    try {
      const session = await firstValueFrom(
        this.authService.getGraphUserTestSession()
      );
      this.userId = session.userId || '';
      this.accountsUserId = session.accountsUserId || '';
      this.email = session.email || '';
      this.graphRequestUrl = session.graphRequestUrl || '';
      this.graphAccessToken = session.graphAccessToken || '';
      this.exchangeWebhookConfigured = Boolean(
        (session as { exchangeWebhookConfigured?: boolean }).exchangeWebhookConfigured
      );

      if (!this.userId || !this.graphAccessToken || !this.graphRequestUrl) {
        throw new Error('Accounts did not return Graph test session fields.');
      }

      await this.fetchGraphUser();
      void this.fetchLookupUserById();
    } catch (err: unknown) {
      const msg =
        (err as { error?: { message?: string } })?.error?.message ||
        (err instanceof Error ? err.message : 'Request failed');
      this.error = msg;
      this.graphUser = null;
    } finally {
      this.loading = false;
    }
  }

  private async fetchGraphUser(): Promise<void> {
    const res = await fetch(this.graphRequestUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.graphAccessToken}`,
      },
    });
    this.graphStatus = res.status;
    const text = await res.text();
    let parsed: GraphUserPayload | null = null;
    try {
      parsed = JSON.parse(text) as GraphUserPayload;
      this.graphResponseJson = JSON.stringify(parsed, null, 2);
    } catch {
      this.graphResponseJson = text;
    }
    if (!res.ok) {
      this.error = `Graph GET returned HTTP ${res.status}`;
      this.graphUser = null;
      return;
    }
    this.graphUser = parsed;
    if (parsed?.mail && !this.oldEmail.trim()) {
      this.oldEmail = parsed.mail;
    }
    this.prefillNewEmailFromIdentities(parsed?.identities);
    if (this.newEmail.trim()) {
      this.previewPatchPayload();
    }
  }

  lookupUserGraphUrl(userId: string): string {
    const id = userId.trim();
    return `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(id)}?$select=${GRAPH_USER_FULL_SELECT}`;
  }

  async fetchLookupUserById(): Promise<void> {
    const id = this.lookupUserId.trim();
    if (!id) {
      this.lookupError = 'Enter a user object id (GUID).';
      return;
    }
    if (!this.graphAccessToken) {
      try {
        const session = await firstValueFrom(
          this.authService.getGraphUserTestSession()
        );
        this.graphAccessToken = session.graphAccessToken || '';
      } catch {
        this.lookupError = 'Sign in and refresh session first.';
        return;
      }
    }
    if (!this.graphAccessToken) {
      this.lookupError = 'No Graph access token available.';
      return;
    }

    this.lookupLoading = true;
    this.lookupError = '';
    this.lookupResponseJson = '';
    this.lookupStatus = null;
    this.lookupRequestUrl = this.lookupUserGraphUrl(id);

    try {
      const res = await fetch(this.lookupRequestUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.graphAccessToken}`,
        },
      });
      this.lookupStatus = res.status;
      const text = await res.text();
      try {
        this.lookupResponseJson = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        this.lookupResponseJson = text;
      }
      if (!res.ok) {
        this.lookupError = `Graph GET returned HTTP ${res.status}`;
      }
    } catch (err: unknown) {
      this.lookupError =
        err instanceof Error ? err.message : 'Lookup request failed.';
    } finally {
      this.lookupLoading = false;
    }
  }

  private prefillNewEmailFromIdentities(identities?: GraphIdentity[]): void {
    if (this.newEmail.trim() || !identities?.length) {
      return;
    }
    const mailIdentity = identities.find(
      (i) =>
        i.signInType === 'federated' &&
        i.issuer?.toLowerCase() === 'mail' &&
        i.issuerAssignedId?.includes('@')
    );
    if (mailIdentity?.issuerAssignedId) {
      this.newEmail = mailIdentity.issuerAssignedId;
    }
  }

  /** Rebuild proxyAddresses: new SMTP: primary, drop old alias, keep other smtp: entries. */
  rebuildProxyAddressesForPrimaryEmail(
    current: string[] | null | undefined,
    newPrimaryEmail: string,
    removeEmails: string[] = []
  ): string[] {
    const primary = newPrimaryEmail.trim().toLowerCase();
    const remove = new Set(
      removeEmails.map((e) => e.trim().toLowerCase()).filter(Boolean)
    );
    const secondary: string[] = [];
    for (const entry of current ?? []) {
      const raw = entry.trim();
      if (!raw) continue;
      const lower = raw.toLowerCase();
      let email = '';
      if (lower.startsWith('smtp:')) {
        email = raw.slice(5).trim().toLowerCase();
      } else {
        secondary.push(raw);
        continue;
      }
      if (!email || email === primary || remove.has(email)) {
        continue;
      }
      secondary.push(`smtp:${email}`);
    }
    const deduped = secondary.filter(
      (a, i, arr) =>
        arr.findIndex((b) => b.toLowerCase() === a.toLowerCase()) === i
    );
    return [`SMTP:${newPrimaryEmail.trim()}`, ...deduped];
  }

  /** PATCH body: mail + federated (issuer mail) + unchanged userPrincipalName identity. */
  buildPatchUserBody(newEmail: string): GraphUserPatchBody {
    const normalized = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      throw new Error('Enter a valid email address.');
    }
    const upnIdentity = (this.graphUser?.identities ?? []).find(
      (i) => i.signInType === 'userPrincipalName'
    );
    if (!upnIdentity) {
      throw new Error(
        'Load Graph GET first — need existing userPrincipalName identity to preserve.'
      );
    }
    const body: GraphUserPatchBody = {
      mail: normalized,
      identities: [
        {
          signInType: 'federated',
          issuer: 'mail',
          issuerAssignedId: normalized,
        },
        {
          signInType: upnIdentity.signInType,
          issuer: upnIdentity.issuer,
          issuerAssignedId: upnIdentity.issuerAssignedId,
        },
      ],
    };
    if (this.includeProxyAddresses) {
      const remove = this.oldEmail.trim()
        ? [this.oldEmail.trim()]
        : (this.graphUser?.mail ? [this.graphUser.mail] : []);
      body.proxyAddresses = this.rebuildProxyAddressesForPrimaryEmail(
        this.graphUser?.proxyAddresses,
        normalized,
        remove
      );
    }
    return body;
  }

  previewPatchPayload(): void {
    this.patchError = '';
    try {
      const body = this.buildPatchUserBody(this.newEmail);
      this.patchRequestJson = JSON.stringify(body, null, 2);
    } catch (err: unknown) {
      this.patchRequestJson = '';
      this.patchError =
        err instanceof Error ? err.message : 'Could not build PATCH body.';
    }
  }

  async patchUserEmail(): Promise<void> {
    if (!this.userId || !this.graphAccessToken) {
      return;
    }
    this.patching = true;
    this.patchError = '';
    this.patchResponseJson = '';
    this.patchStatus = null;
    try {
      const body = this.buildPatchUserBody(this.newEmail);
      this.patchRequestJson = JSON.stringify(body, null, 2);

      const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(this.userId)}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.graphAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      this.patchStatus = res.status;
      const text = await res.text();
      if (text) {
        try {
          this.patchResponseJson = JSON.stringify(JSON.parse(text), null, 2);
        } catch {
          this.patchResponseJson = text;
        }
      } else {
        this.patchResponseJson = res.ok
          ? '(empty body — Graph often returns 204 No Content on success)'
          : '';
      }
      if (!res.ok) {
        this.patchError = `Graph PATCH returned HTTP ${res.status}`;
        return;
      }
      await this.fetchGraphUser();
    } catch (err: unknown) {
      this.patchError =
        err instanceof Error ? err.message : 'PATCH request failed.';
    } finally {
      this.patching = false;
    }
  }

  async patchProxyAddressesOnly(): Promise<void> {
    if (!this.userId || !this.graphAccessToken || !this.newEmail.trim()) {
      return;
    }
    this.patching = true;
    this.patchError = '';
    this.patchResponseJson = '';
    this.patchStatus = null;
    try {
      const normalized = this.newEmail.trim().toLowerCase();
      const remove = this.oldEmail.trim()
        ? [this.oldEmail.trim()]
        : (this.graphUser?.mail ? [this.graphUser.mail] : []);
      const proxyAddresses = this.rebuildProxyAddressesForPrimaryEmail(
        this.graphUser?.proxyAddresses,
        normalized,
        remove
      );
      this.patchRequestJson = JSON.stringify({ proxyAddresses }, null, 2);
      const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(this.userId)}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.graphAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ proxyAddresses }),
      });
      this.patchStatus = res.status;
      const text = await res.text();
      if (text) {
        try {
          this.patchResponseJson = JSON.stringify(JSON.parse(text), null, 2);
        } catch {
          this.patchResponseJson = text;
        }
      } else {
        this.patchResponseJson = res.ok
          ? '(empty body — Graph often returns 204 No Content on success)'
          : '';
      }
      if (!res.ok) {
        this.patchError = `Graph PATCH returned HTTP ${res.status}`;
        return;
      }
      await this.fetchGraphUser();
    } catch (err: unknown) {
      this.patchError =
        err instanceof Error ? err.message : 'PATCH request failed.';
    } finally {
      this.patching = false;
    }
  }

  async triggerExchangeCleanup(): Promise<void> {
    const oldAddr = this.oldEmail.trim() || this.graphUser?.mail?.trim() || '';
    const newAddr = this.newEmail.trim();
    if (!oldAddr || !newAddr) {
      this.exchangeError = 'Set old and new email first.';
      return;
    }
    this.exchangeLoading = true;
    this.exchangeError = '';
    this.exchangeResponseJson = '';
    try {
      const result = await firstValueFrom(
        this.authService.runGraphUserTestExchangeCleanup({
          oldEmail: oldAddr,
          newEmail: newAddr,
          waitMs: 10_000,
        })
      );
      this.exchangeResponseJson = JSON.stringify(result, null, 2);
    } catch (err: unknown) {
      const msg =
        (err as { error?: { message?: string } })?.error?.message ||
        (err instanceof Error ? err.message : 'Exchange cleanup failed');
      this.exchangeError = msg;
    } finally {
      this.exchangeLoading = false;
    }
  }

  async patchThenExchangeCleanup(): Promise<void> {
    await this.patchUserEmail();
    if (this.patchError) {
      return;
    }
    await this.triggerExchangeCleanup();
  }
}
